import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CreateTicketComponent } from '@tesla/ng-srm';
import { TslDialog, TslToastrConfig, TslToastrService } from '@tesla/ng-tds';
import { AppSettings } from 'app/app.settings';
import { LanguageService } from 'app/core/services/language.service';
import { StatusEnum, StatusMessage } from 'app/profile/models/state.model';
import { StateService } from 'app/profile/services/state.service';
import { I18nService } from 'app/shared/services/i18n.service';
import { SpinnerService } from 'app/shared/services/spinner.service';
import { SrmApiService } from 'app/shared/services/srm-api.service';
import { WebComponentLoaderService } from 'app/shared/services/web-component-loader.service';
import { ReplaySubject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, OnDestroy {
    showSpinner = false;
    statusMessage = new StatusMessage();
    useProdEnv: boolean = false;
    env: string = 'DEV';
    isSupplierTicketingEnabled = false;
    isChatbotEnabledFromApi = false;
    isChatbotEnabledForCurrentRoute = false;
    isChatbotEnabledForCurrentLocale = false;
    newLocale = '';
    skipChatbotOnRoutes = ['/Onboarding'];
    isChatbotEnabled = false;
    authTokenPath;
    chatbotApiUrl;
    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(
        private spinnerService: SpinnerService,
        private stateService: StateService,
        private toastr: TslToastrService,
        private translate: I18nService,
        public appSettings: AppSettings,
        private scriptLoader: WebComponentLoaderService,
        private languageService: LanguageService,
        private srmApiService: SrmApiService,
        private dialog: TslDialog,
        private router: Router,
    ) {
        this.spinnerService.statusChange$.pipe(delay(100)).subscribe((data) => {
            this.showSpinner = data;
        });
        this.authTokenPath = `${this.appSettings?.openid?.id}:token` || 'supplier-profile:token';
        this.setStateWatcher();
    }

    ngOnInit(): void {
        this.useProdEnv = localStorage.getItem('use-prod-env') == 'true';
        this.env = this.appSettings.Environment;

        this.loadWebComponents();
        this.chatbotApiUrl = this.appSettings.SRM_API_URL + '/chat';
        this.isChatbotEnabledForCurrentRoute = !this.skipChatbotOnRoutes.some((route) =>
            window.location.href.includes(route),
        );

        // this.authSub = this.authData.getAccessTokenAsync().subscribe(res => {
        // 	if(res) {
        // 		this.hasToken = true;
        // 		this.userSub = this.profileService.getUserDetails().subscribe(res => {
        // 			if(res?.supplierContact?.languageCode) this.languageService.changeLanguage(res.supplierContact.languageCode);
        // 		});
        // 	}
        // 	else this.hasToken = false;
        // });

        // Enable or Disable "Submit a ticket" feature
        this.srmApiService.getSetting<boolean>('IsSupplierTicketingEnabled').subscribe((res) => {
            this.isSupplierTicketingEnabled = res;
        });

        this.srmApiService.getSetting<boolean>('IsChatbotEnabled').subscribe((res) => {
            this.isChatbotEnabledFromApi = res;
            if (res) {
                //    Todo: Remove/update the following code to extend the support to non en-US locale
                this.languageService
                    .getSelectedLanguageAsync()
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((newLocale) => {
                        this.newLocale = newLocale;
                        this.isChatbotEnabledForCurrentLocale = this.newLocale == 'en-US';
                        this.checkIfChatbotIsEnabled();
                    });
                this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((val) => {
                    this.isChatbotEnabledForCurrentRoute = !this.skipChatbotOnRoutes.some((route) =>
                        window.location.href.includes(route),
                    );
                    this.checkIfChatbotIsEnabled();
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    checkIfChatbotIsEnabled() {
        this.isChatbotEnabled =
            this.isChatbotEnabledFromApi &&
            this.isChatbotEnabledForCurrentLocale &&
            this.isChatbotEnabledForCurrentRoute;
    }

    loadWebComponents(): void {
        let srmWcBaseUrl = '';
        if (this.appSettings.Production) {
            srmWcBaseUrl =
                'https://digitalassets.tesla.com/axle-tesla/raw/upload/Prod/Supplier%20Relationship%20Management';
        } else {
            srmWcBaseUrl =
                'https://digitalassets.tesla.com/axle-tesla/raw/upload/Stage/Supplier%20Relationship%20Management';
        }
        // Load SRM task from cloudinary
        this.scriptLoader.loadUrl('webpackJsonpSrmTaskWebComponent', `${srmWcBaseUrl}/srm-task/0.0.2/srm-task.js`);
        // Load SRM chatbot from cloudinary
        this.scriptLoader.loadUrl(
            'webpackJsonpSrmChatbotWebComponent',
            `${srmWcBaseUrl}/srm-chatbot/0.0.1/srm-chatbot.js`,
        );
    }

    private setStateWatcher(): void {
        const config: TslToastrConfig = {
            duration: 4000,
            hasIcon: true,
        };

        this.stateService.currentStatus.pipe(delay(500)).subscribe((status) => {
            if (status.type === StatusEnum.Success) {
                this.toastr.success(
                    status.message || this.statusMessage.SuccessMessage,
                    this.statusMessage.SuccessTitle,
                    config,
                );
            } else if (status.type === StatusEnum.Warning) {
                this.toastr.warning(
                    status.message || this.statusMessage.WarningMessage,
                    this.statusMessage.WarningTitle,
                    config,
                );
            } else if (status.type === StatusEnum.Error) {
                this.toastr.error(
                    status.message || this.statusMessage.ErrorMessage,
                    this.statusMessage.ErrorTitle,
                    config,
                );
            } else if (status.type === StatusEnum.InvalidSupplier) {
                this.toastr.warning(
                    status.message || this.statusMessage.InvalidSupplierMessage,
                    this.statusMessage.InvalidSupplierTitle,
                    config,
                );
            }
        });
    }

    onSuggestedTransferToSupport(event) {
        console.log(event);
    }

    switchTranslateEnv(event) {
        this.useProdEnv = event?.checked;
        this.languageService.setUseProdEnv(this.useProdEnv);
    }

    private setMessages(): void {
        this.translate.getAsync('SuccessTitle').subscribe((res) => (this.statusMessage.SuccessTitle = res));
        this.translate.getAsync('SuccessMessage').subscribe((res) => (this.statusMessage.SuccessMessage = res));
        this.translate.getAsync('WarningTitle').subscribe((res) => (this.statusMessage.WarningTitle = res));
        this.translate.getAsync('WarningMessage').subscribe((res) => (this.statusMessage.WarningMessage = res));
        this.translate.getAsync('ErrorTitle').subscribe((res) => (this.statusMessage.ErrorTitle = res));
        this.translate.getAsync('ErrorMessage').subscribe((res) => (this.statusMessage.ErrorMessage = res));
        this.translate
            .getAsync('InvalidSupplierTitle')
            .subscribe((res) => (this.statusMessage.InvalidSupplierTitle = res));
        this.translate
            .getAsync('InvalidSupplierMessage')
            .subscribe((res) => (this.statusMessage.InvalidSupplierMessage = res));
    }

    onSubmitTicket() {
        const dialogRef = this.dialog.open(CreateTicketComponent, {
            panelClass: ['sidepanel', 'slideIn', 'animate__animated', 'animate__slideInRight'],
            maxWidth: '800px',
            height: '100%',
            minWidth: '414px',
            data: {},
        });
        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
            }
        });
    }
}
